import { z } from 'zod';
import { ClientProjectActivityStatusSchema } from './client.js';
import { CalendarDateOnlySchema, ClientProjectStatusSchema, PaginationQuerySchema, createPaginatedResponseSchema } from './common.js';
import { InvoiceStatusSchema } from './invoice.js';
// =============================================================================
// Client and matter overview: the hero figures
// =============================================================================
/**
 * The figures that need an active BUSINESS ledger. `paidThisFinancialYear`
 * carries the window the server used, so a sub-line reads the real start date
 * instead of recomputing the financial year in the browser.
 */
export const OverviewLedgerFiguresSchema = z.object({
    currency: z.string(),
    outstandingMinorUnits: z.int(),
    paidThisFinancialYear: z.object({
        amountMinorUnits: z.int(),
        count: z.int(),
        from: CalendarDateOnlySchema,
        to: CalendarDateOnlySchema,
    }),
});
/**
 * The client's ledger figures plus its unallocated credit: money received on
 * account and not yet applied to an invoice (positive, floored at zero). It is
 * already netted into `outstandingMinorUnits`, so it prompts the user to
 * allocate the payment.
 */
export const ClientOverviewLedgerFiguresSchema = OverviewLedgerFiguresSchema.extend({
    unallocatedCreditMinorUnits: z.int(),
});
export const ClientOverviewResponseSchema = z.object({
    wipTotalMinorUnits: z.int(),
    /** Debit-positive, so a negative figure is money held for the client; null with no active TRUST ledger. */
    trustBalanceMinorUnits: z.int().nullable(),
    /** Null with no active BUSINESS ledger. */
    ledger: ClientOverviewLedgerFiguresSchema.nullable(),
});
export const ClientProjectOverviewResponseSchema = z.object({
    wipTotalMinorUnits: z.int(),
    /** Null with no active BUSINESS ledger. */
    ledger: OverviewLedgerFiguresSchema.nullable(),
});
// =============================================================================
// Client matters summary: one row per matter with its headline figures
// =============================================================================
export const ClientProjectsSummarySortSchema = z.enum(['lastActivity', 'wip', 'outstanding', 'name', 'reference']);
export const ClientProjectsSummaryQuerySchema = z.strictObject({
    /** Case-insensitive contains over name, internal reference and custom reference. */
    search: z.string().trim().min(1).max(200).optional(),
    /** `active` = ACTIVE; `inactive` = any other status. */
    status: z.enum(['active', 'inactive', 'all']).default('active'),
    /** Absent = the default order: ACTIVE first, then latest activity, then name. */
    sort: ClientProjectsSummarySortSchema.optional(),
    /** Defaults to descending for figures and last activity, ascending for name and reference. */
    direction: z.enum(['asc', 'desc']).optional(),
    limit: PaginationQuerySchema.shape.limit,
    /** Opaque; encodes the offset into the sorted list. */
    cursor: z.string().optional(),
});
export const ClientProjectSummaryRowSchema = z.object({
    id: z.string(),
    name: z.string(),
    internalReference: z.string(),
    customReference: z.string().nullable(),
    status: ClientProjectStatusSchema,
    wipTotalMinorUnits: z.int(),
    /** The matter's own currency, which `wipTotalMinorUnits` is in; present with or without a ledger. */
    wipCurrency: z.string(),
    /** Null with no active BUSINESS ledger. */
    outstandingMinorUnits: z.int().nullable(),
    lastActivityDate: z.iso.date().nullable(),
});
export const ClientProjectsSummaryResponseSchema = z.object({
    data: z.array(ClientProjectSummaryRowSchema),
    /** The base currency of the ledger figures; null with no active BUSINESS ledger. */
    currency: z.string().nullable(),
    /** Matters after the filter and before paging. */
    totalCount: z.int(),
    hasMore: z.boolean(),
    nextCursor: z.string().nullable(),
});
// =============================================================================
// Payments received and expense count: the timeline chip counts
// =============================================================================
/** Money received over all time. `received` is null with no active BUSINESS ledger, so the chip drops out. */
export const PaymentsReceivedResponseSchema = z.object({
    received: z
        .object({
        amountMinorUnits: z.int(),
        count: z.int(),
        currency: z.string(),
    })
        .nullable(),
});
/** Billable (APPROVED and PAID) expenses attributed to the scope. */
export const ExpenseCountResponseSchema = z.object({ count: z.int() });
// =============================================================================
// Timeline: one feed of a client's or matter's activities, invoices, payments,
// expenses and emails
// =============================================================================
/** The timeline's sources, in merge rank: within a day, rows group in this order. */
export const TIMELINE_SOURCES = ['activity', 'invoice', 'payment', 'expense', 'email'];
export const TimelineSourceSchema = z.enum(TIMELINE_SOURCES);
/** Invoices the timeline lists: issued, paid and voided, never drafts. */
export const TIMELINE_INVOICE_STATUSES = ['APPROVED', 'SENT', 'PAID', 'VOIDED'];
export const TimelineQuerySchema = z.strictObject({
    /** Repeatable. Absent = every source. */
    sources: z.array(TimelineSourceSchema).min(1).optional(),
    limit: z.coerce.number().int().min(1).max(100).prefault(25),
    /** Opaque; pass the previous page's `nextCursor` unchanged, with the same `sources`. */
    cursor: z.string().max(2048).optional(),
});
const TimelineItemCommonShape = {
    id: z.string(),
    /** The row's own date as an ISO instant. */
    date: z.iso.datetime(),
    /** The calendar day the row sits under: the UTC date, or the South African date for an email's instant. */
    day: CalendarDateOnlySchema,
    /** The matter, archived or not; null when the row has none. */
    clientProject: z.object({ id: z.string(), name: z.string() }).nullable(),
};
export const TimelineItemSchema = z.discriminatedUnion('type', [
    z.object({
        ...TimelineItemCommonShape,
        type: z.literal('activity'),
        description: z.string(),
        status: ClientProjectActivityStatusSchema,
        amountMinorUnits: z.int().nullable(),
        currency: z.string(),
    }),
    z.object({
        ...TimelineItemCommonShape,
        type: z.literal('invoice'),
        invoiceNumber: z.string().nullable(),
        status: InvoiceStatusSchema,
        totalMinorUnits: z.int(),
        currency: z.string(),
        dueDate: z.string().nullable(),
    }),
    z.object({
        ...TimelineItemCommonShape,
        type: z.literal('payment'),
        /** At matter scope, the share allocated to the matter's invoices. */
        amountMinorUnits: z.int(),
        currency: z.string(),
        /** The invoice the payment settled when it settled exactly one. */
        invoiceId: z.string().nullable(),
    }),
    z.object({
        ...TimelineItemCommonShape,
        type: z.literal('expense'),
        supplierName: z.string(),
        supplierInvoiceNumber: z.string().nullable(),
        totalMinorUnits: z.int(),
        currency: z.string(),
        /** Whether an invoice line already bills this expense. */
        billed: z.boolean(),
    }),
    z.object({
        ...TimelineItemCommonShape,
        type: z.literal('email'),
        subject: z.string(),
        direction: z.enum(['sent', 'received']),
    }),
]);
export const TimelineResponseSchema = createPaginatedResponseSchema(TimelineItemSchema);
